import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart, MapPin, Clock, Thermometer } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function DestinationCard({ d, isFav, onToggleFav, className = "" }) {
  const { user } = useAuth();
  const [weather, setWeather] = useState(null);
  const images = Array.from(new Set([d.image_url, ...(d.gallery || [])].filter(Boolean)));
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const q = d.city || d.country || "";
    if (!q) return;
    api.get("/weather", { params: { city: q } })
      .then(({ data }) => { if (!cancelled) setWeather(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [d.city, d.country]);

  useEffect(() => {
    setCurrentImage(0);
  }, [d.id, d.image_url, d.gallery]);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = window.setInterval(() => {
      setCurrentImage((index) => (index + 1) % images.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [images.length]);

  const handleFav = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please log in to save favorites"); return; }
    onToggleFav?.(d);
  };

  return (
    <Link
      to={`/destination/${d.slug}/`}
      data-testid={`dest-card-${d.slug}`}
      className={`dest-card group relative overflow-hidden rounded-lg border border-border bg-card block ${className}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          key={images[currentImage]}
          src={images[currentImage] || d.image_url}
          alt={d.name}
          className="dest-card-img h-full w-full object-cover"
          loading="lazy"
          style={{ animation: images.length > 1 ? "destCardFade 700ms ease-in-out" : undefined }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" aria-label={`${images.length} itinerary photos`}>
            {images.map((_, index) => (
              <span key={index} className={`h-1.5 w-1.5 rounded-full transition ${index === currentImage ? "bg-white" : "bg-white/40"}`} />
            ))}
          </div>
        )}
        <button
          onClick={handleFav}
          data-testid={`fav-btn-${d.slug}`}
          aria-label="Toggle favorite"
          className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center transition hover:bg-black/60"
        >
          <Heart className={`h-4 w-4 ${isFav ? "fill-accent text-accent" : "text-white"}`} />
        </button>
        {weather && (
          <div className="absolute top-3 left-3 rounded-full bg-black/40 backdrop-blur-md text-white px-3 py-1 text-[11px] flex items-center gap-1.5" data-testid={`weather-${d.slug}`}>
            <Thermometer className="h-3 w-3" /> {weather.temperature_c}°C · {weather.condition}
          </div>
        )}
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="text-[11px] uppercase tracking-widest opacity-90">{d.country || d.category}</div>
          <h3 className="heading-serif text-2xl font-semibold leading-tight">{d.name}</h3>
        </div>
      </div>
      <div className="p-4 flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground flex items-center gap-3">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {d.duration_days}D / {d.duration_nights}N</span>
          {d.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {d.city}</span>}
        </div>
        {Number(d.price) > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">From</div>
            <div className="text-lg font-bold">₹{Number(d.price).toLocaleString("en-IN")}</div>
          </div>
        )}
      </div>
    </Link>
  );
}
